// ImagePickerCompressor.jsx
import { useRef, useState, forwardRef, useImperativeHandle } from "react";
import imageCompression from "browser-image-compression";

// This is now a controlled component. It holds almost no state of its own.
// The parent component is responsible for managing the state and passing it down as props.
const ImagePickerCompressor = forwardRef(({
  onChange,
  onClear,
  options = {},
  buttonLabel = "Attach image",
  className = "",
  // Controlled component props:
  previewUrl,
  selectedName,
  savingsText,
  compressionProgress,
  error,
}, ref) => {
  const fileInputRef = useRef(null);
  const [isCompressing, setIsCompressing] = useState(false);

  const pick = () => fileInputRef.current?.click();

  useImperativeHandle(ref, () => ({
    pick,
  }));

  const getDims = (file) =>
    new Promise((resolve) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        const w = img.naturalWidth, h = img.naturalHeight;
        URL.revokeObjectURL(url);
        resolve({ w, h });
      };
      img.src = url;
    });

  const compressSmart = async (file, onProgress) => {
    const {
      maxSizeMB = 0.8,
      maxWidthOrHeight = 1280,
      minCompressBytes = 0,
      convertTo = "jpeg",
      skipGif = true,
      force = true,
      initialQuality = 0.7,
    } = options;

    const isGif = file.type === "image/gif";
    if (skipGif && isGif) {
      return { file, skipped: true };
    }

    const toWebp = convertTo === "webp" || (convertTo === "auto" && (file.type === "image/png" || file.type === "image/webp"));
    const targetType = toWebp ? "image/webp" : "image/jpeg";

    let should = force || file.size > minCompressBytes;
    try {
      const { w, h } = await getDims(file);
      if (w > maxWidthOrHeight || h > maxWidthOrHeight) should = true;
    } catch { /* ignore */ }

    if (!should) return { file, skipped: true };

    const compressedBlob = await imageCompression(file, {
      maxSizeMB,
      maxWidthOrHeight,
      useWebWorker: true,
      fileType: targetType,
      initialQuality,
      maxIteration: 10,
      onProgress,
    });

    let name = file.name;
    if (targetType === "image/jpeg" && !/\.jpe?g$/i.test(name)) {
      name = name.replace(/\.(png|webp|heic|heif|gif)$/i, "") + ".jpg";
    } else if (targetType === "image/webp" && !/\.webp$/i.test(name)) {
      name = name.replace(/\.(png|jpe?g|heic|heif|gif)$/i, "") + ".webp";
    }

    const compressedFile = new File([compressedBlob], name, { type: targetType });
    const winner = compressedFile.size < file.size ? compressedFile : file;
    const skipped = winner === file;
    return { file: winner, skipped };
  };

  const onFileChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      onChange({ error: "Please select an image file." });
      return;
    }

    setIsCompressing(true);

    try {
      const { file: out, skipped } = await compressSmart(f, (pct) => {
        onChange({ compressionProgress: Math.round(pct) });
      });

      onChange({
        file: out,
        originalFile: f,
        previewUrl: URL.createObjectURL(f), // Parent must revoke this
        originalBytes: f.size,
        compressedBytes: out.size,
        skipped,
        error: null,
        compressionProgress: 100,
      });
    } catch (err) {
      console.error(err);
      onChange({
        file: f,
        originalFile: f,
        previewUrl: URL.createObjectURL(f),
        originalBytes: f.size,
        compressedBytes: f.size,
        skipped: true,
        error: "Compression failed. Using original file.",
        compressionProgress: 0,
      });
    } finally {
      setIsCompressing(false);
      // Reset file input so user can select the same file again
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <div className={`ipc ${className}`}>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={onFileChange}
        disabled={isCompressing}
      />

      {!previewUrl ? (
        <button type="button" className="ipc-btn" onClick={pick} aria-label={buttonLabel} disabled={isCompressing}>
          {isCompressing ? "Processing..." : `📎 ${buttonLabel}`}
        </button>
      ) : (
        <div className="ipc-preview">
          <img src={previewUrl} alt="preview" className="ipc-thumb" />
          <div className="ipc-meta">
            <div className="ipc-name" title={selectedName}>{selectedName}</div>

            {compressionProgress > 0 && compressionProgress < 100 && (
              <div className="ipc-progress"><div className="ipc-bar" style={{ width: `${compressionProgress}%` }} /></div>
            )}

            {!!savingsText && <div className="ipc-savings">{savingsText}</div>}

            <div className="ipc-actions">
              <button type="button" className="ipc-repick" onClick={pick}>Change</button>
              <button type="button" className="ipc-remove" onClick={onClear}>Remove</button>
            </div>
          </div>
        </div>
      )}

      {error && <div className="ipc-error">{error}</div>}
    </div>
  );
});

export default ImagePickerCompressor;